import { motion } from 'framer-motion';
import { Smartphone, Rocket, Clock, Code2, Repeat, Palette, Shield } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

const features = [
  {
    icon: <Code2 className="w-6 h-6" />,
    title: "React Native Expertise",
    description: "Professional cross-platform development using modern technologies"
  },
  {
    icon: <Shield className="w-6 h-6" />,
    title: "Enterprise Quality",
    description: "Robust, secure, and scalable mobile applications"
  },
  {
    icon: <Clock className="w-6 h-6" />,
    title: "Rapid Development",
    description: "Efficient development process to launch your MVP quickly"
  },
  {
    icon: <Repeat className="w-6 h-6" />,
    title: "Cross-Platform",
    description: "Single codebase for both iOS and Android platforms"
  }
];

const ProcessStep = ({ number, icon, title, description }) => {
  const { theme } = useTheme();
  
  return (
    <div className="relative flex items-start gap-4">
      <div className={`flex-shrink-0 w-12 h-12 ${
        theme === 'dark' ? 'bg-blue-500/20' : 'bg-blue-100'
      } rounded-full flex items-center justify-center`}>
        {icon}
      </div>
      <div>
        <div className="absolute -top-2 -left-2 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-sm font-bold text-white">
          {number}
        </div>
        <h4 className={`text-lg font-semibold mb-2 ${
          theme === 'dark' ? 'text-white' : 'text-gray-900'
        }`}>{title}</h4>
        <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
          {description}
        </p>
      </div>
    </div>
  );
};

const StoreDeployment = () => {
  const { theme } = useTheme();
  
  return (
    <div className={`${
      theme === 'dark' ? 'bg-gray-800/50' : 'bg-gray-50'
    } rounded-xl p-6 mt-8`}>
      <h4 className={`text-xl font-semibold mb-4 ${
        theme === 'dark' ? 'text-white' : 'text-gray-900'
      }`}>App Store Deployment</h4>
      <div className="flex flex-col sm:flex-row gap-6">
        <div className="flex items-center gap-4 flex-1">
          <div className={`p-3 rounded-xl ${
            theme === 'dark' ? 'bg-gray-700' : 'bg-white'
          }`}>
            <img 
              src="https://upload.wikimedia.org/wikipedia/commons/6/67/App_Store_%28iOS%29.svg" 
              alt="Apple App Store"
              className="w-8 h-8"
            />
          </div>
          <div>
            <h5 className={`font-semibold mb-1 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>App Store</h5>
            <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
              We handle the entire iOS app submission process
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4 flex-1">
          <div className={`p-3 rounded-xl ${
            theme === 'dark' ? 'bg-gray-700' : 'bg-white'
          }`}>
            <img 
              src="https://upload.wikimedia.org/wikipedia/commons/d/d0/Google_Play_Arrow_logo.svg" 
              alt="Google Play Store"
              className="w-8 h-8"
            />
          </div>
          <div>
            <h5 className={`font-semibold mb-1 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>Google Play</h5>
            <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
              Full Android deployment and publishing support
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const AppPreview = () => {
  const { theme } = useTheme();
  
  return (
    <div className="relative w-[280px] h-[580px] mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className={`absolute inset-0 rounded-[45px] ${
          theme === 'dark' ? 'bg-gray-700' : 'bg-white'
        } shadow-2xl overflow-hidden`}
      >
        {/* Dynamic Island */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[120px] h-[35px] bg-black rounded-b-[20px] flex items-center justify-center z-20">
          <div className="w-[85px] h-[24px] bg-black rounded-[18px] flex items-center justify-between px-3">
            <div className="w-3 h-3 rounded-full bg-gray-800" />
            <div className="w-2 h-2 rounded-full bg-gray-800" />
          </div>
        </div>

        {/* App Content */}
        <div className="absolute top-[35px] left-0 right-0 bottom-0 overflow-hidden">
          {/* App Header */}
          <motion.div
            initial={{ y: -50 }}
            animate={{ y: 0 }}
            transition={{ delay: 1, duration: 0.5 }}
            className={`p-4 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} border-b ${
              theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
            }`}
          >
            <div className="flex items-center justify-between">
              <h3 className={`text-lg font-semibold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>Dashboard</h3>
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full ${
                  theme === 'dark' ? 'bg-blue-500/20' : 'bg-blue-100'
                } flex items-center justify-center`}>
                  <Smartphone className="w-4 h-4 text-blue-500" />
                </div>
              </div>
            </div>
          </motion.div>

          {/* Stats Section */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5 }}
            className="p-4"
          >
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className={`p-4 rounded-xl ${
                theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-100'
              }`}>
                <div className={`text-sm ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>Revenue</div>
                <div className={`text-xl font-bold ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>$12,486</div>
                <motion.div
                  className="h-1 bg-blue-500 mt-2 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: '80%' }}
                  transition={{ delay: 2, duration: 1 }}
                />
              </div>
              <div className={`p-4 rounded-xl ${
                theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-100'
              }`}>
                <div className={`text-sm ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>Users</div>
                <div className={`text-xl font-bold ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>1,284</div>
                <motion.div
                  className="h-1 bg-green-500 mt-2 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: '60%' }}
                  transition={{ delay: 2.2, duration: 1 }}
                />
              </div>
            </div>

            {/* Activity Feed */}
            <div className={`rounded-xl p-4 ${
              theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-100'
            }`}>
              <h4 className={`text-sm font-semibold mb-3 ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>Recent Activity</h4>
              <div className="space-y-3">
                {[1, 2, 3].map((_, i) => (
                  <motion.div
                    key={i}
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 2.5 + (i * 0.2) }}
                    className={`flex items-center gap-3 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}
                  >
                    <div className={`w-2 h-2 rounded-full ${
                      i === 0 ? 'bg-green-500' : i === 1 ? 'bg-blue-500' : 'bg-purple-500'
                    }`} />
                    <div className="text-sm">
                      {i === 0 ? 'New user signup' : i === 1 ? 'Payment received' : 'Task completed'}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Chart */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 3 }}
              className={`mt-4 p-4 rounded-xl ${
                theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-100'
              }`}
            >
              <div className="h-[100px] flex items-end gap-2">
                {[40, 70, 45, 90, 65, 45, 80].map((height, i) => (
                  <motion.div
                    key={i}
                    className="flex-1 bg-blue-500 rounded-t"
                    initial={{ height: 0 }}
                    animate={{ height: `${height}%` }}
                    transition={{ delay: 3.2 + (i * 0.1), duration: 0.5 }}
                  />
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Bottom Indicator */}
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-[120px] h-1 bg-gray-800 rounded-full" />
      </motion.div>
    </div>
  );
};

export default function MobileAppSection() {
  const { theme } = useTheme();

  return (
    <section id="mobile-app-section" className={`py-20 px-4 ${
      theme === 'dark' ? 'bg-gray-900' : 'bg-white'
    }`}>
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <Smartphone className="w-12 h-12 text-blue-500" />
          <h2 className={`text-3xl font-bold ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>Mobile App Development</h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-16">
          <div className="space-y-8">
            <div className="prose prose-invert max-w-none">
              <p className={`text-lg ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}>
                Transform your ideas into professional mobile applications built with React Native. Our expert development team creates beautiful, high-performance apps for both iOS and Android platforms.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`${
                    theme === 'dark' ? 'bg-gray-800' : 'bg-white shadow-lg'
                  } p-6 rounded-xl`}
                >
                  <div className="text-blue-500 mb-4">{feature.icon}</div>
                  <h3 className={`text-xl font-semibold mb-2 ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>{feature.title}</h3>
                  <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                    {feature.description}
                  </p>
                </motion.div>
              ))}
            </div>

            <StoreDeployment />
          </div>

          <div className="lg:sticky lg:top-24">
            <AppPreview />
          </div>
        </div>

        <div className={`${
          theme === 'dark' ? 'bg-gray-800' : 'bg-white shadow-lg'
        } rounded-xl p-8`}>
          <h3 className={`text-2xl font-bold mb-8 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>Development Process</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <ProcessStep
              number={1}
              icon={<Palette className="w-6 h-6 text-blue-400" />}
              title="Requirements & Design"
              description="We work with you to define features and create the perfect UI/UX."
            />
            <ProcessStep
              number={2}
              icon={<Code2 className="w-6 h-6 text-blue-400" />}
              title="Development"
              description="Our team builds your app using modern React Native practices."
            />
            <ProcessStep
              number={3}
              icon={<Shield className="w-6 h-6 text-blue-400" />}
              title="Testing & QA"
              description="Rigorous testing ensures your app works flawlessly."
            />
            <ProcessStep
              number={4}
              icon={<Rocket className="w-6 h-6 text-blue-400" />}
              title="Deploy & Launch"
              description="We handle submission to App Store and Google Play."
            />
          </div>
        </div>
      </div>
    </section>
  );
}